import { useRequestExampleValue } from '@/business-components/request-parameter/hooks/useRequestExampleValue';
import usePersistFn from '@/hooks/usePersistFn';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useOperationConfigModelStore } from '@/models/operation-config/operationConfig';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import {
  getSampleCodeSelectedLanguageVariant,
  setSampleCodeSelectedLanguageVariant,
} from '@/shared/constant/local-storage/sample-code-selected-language-variant/SampleCodeSelectedLanguageVariant';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import {
  defaultSampleCodeLanguage,
  generateRequestSampleCode,
} from '@/shared/util/generateRequestSampleCode';
import React from 'react';

export function useSelectSampleCodeValue(
  value: HttpApiServerType,
  extraData: ResourceHttpApiData['extraData'],
) {
  const selectedEnvironment = useDocsDataModelStore((x) => x.environments.selectedEnvironment);

  const [codeValue, setCode] = React.useState(extraData.defaultRequestSampleCode?.code);
  const [codeBlockLanguage, setCodeBlockLanguage] = React.useState(
    extraData.defaultRequestSampleCode?.codeBlockLanguage,
  );

  const selectedSampleCodeLanguage = useOperationConfigModelStore(
    (x) => x.selectedSampleCodeLanguage,
  );
  const selectedSampleCodeVariant = useOperationConfigModelStore(
    (x) => x.selectedSampleCodeVariant,
  );
  const setSelectedSampleCodeLanguageVariant = useOperationConfigModelStore(
    (x) => x.setSelectedSampleCodeLanguageVariant,
  );
  const { selectedRequestExample, requestExamples, setSelectedRequestKey } = useRequestExampleValue(
    value.requestBody.examples,
  );

  const generateRequestSampleCodeFn = usePersistFn(
    async (options: { language: string; variant: string } | undefined) => {
      if (
        (options &&
          (options.language !== extraData.defaultRequestSampleCode?.language ||
            options.variant !== extraData.defaultRequestSampleCode?.variant)) ||
        selectedRequestExample?.key !== requestExamples[0]?.key
      ) {
        try {
          const defaultRequestSampleCode = await generateRequestSampleCode(
            value,
            extraData.requestCollection,
            selectedEnvironment?.variables ?? [],
            options,
            selectedRequestExample?.value,
          );
          setCode(defaultRequestSampleCode?.code);
          setCodeBlockLanguage(defaultRequestSampleCode?.codeBlockLanguage);
          setSelectedSampleCodeLanguageVariant(options);
          return;
        } catch (error) {
          console.error(error);
        }
      }

      setCode(extraData.defaultRequestSampleCode?.code);
      setCodeBlockLanguage(extraData.defaultRequestSampleCode?.codeBlockLanguage);
      setSelectedSampleCodeLanguageVariant(options);
    },
  );

  React.useEffect(() => {
    const localLanguageVariant = getSampleCodeSelectedLanguageVariant();
    generateRequestSampleCodeFn(localLanguageVariant);
  }, [generateRequestSampleCodeFn, value, extraData, selectedRequestExample]);

  const setSelectedSampleCodeLanguage = usePersistFn(async (language: string | undefined) => {
    if (language) {
      const currentLanguage = value.codeSampleLanguages.find((x) => x.key === language);
      const currentVariant = currentLanguage?.variants.find((x) => x.key && !x.id)?.key;

      if (currentLanguage && currentVariant) {
        await generateRequestSampleCodeFn({
          language,
          variant: currentVariant,
        });
        setSampleCodeSelectedLanguageVariant(language, currentVariant);
        return;
      }
    }

    generateRequestSampleCodeFn(undefined);
    setSampleCodeSelectedLanguageVariant(undefined, undefined);
  });

  const setSelectedSampleCodeVariant = usePersistFn(async (variant: string) => {
    const language = selectedSampleCodeLanguage ?? defaultSampleCodeLanguage;
    await generateRequestSampleCodeFn({
      language,
      variant,
    });
    setSampleCodeSelectedLanguageVariant(language, variant);
  });

  const selectedLanguageVariants = React.useMemo(() => {
    const language = selectedSampleCodeLanguage ?? defaultSampleCodeLanguage;

    return value.codeSampleLanguages.find((x) => x.key === language)?.variants ?? [];
  }, [selectedSampleCodeLanguage, value.codeSampleLanguages]);

  return {
    codeValue,
    codeBlockLanguage,
    selectedSampleCodeLanguage,
    selectedSampleCodeVariant,
    setSelectedSampleCodeLanguage,
    setSelectedSampleCodeVariant,
    selectedLanguageVariants,
    selectedRequestExample,
    requestExamples,
    setSelectedRequestKey,
  };
}
